import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  AppCard,
  AppInput,
  AppInputNumber,
  AppModal,
  AppRemixIcon,
  AppSingleSelect,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import colors from '@/constants/colors';
import Translator from '../../../../components/helpers/Translator';
import * as currentPageAction from '@/store/actions/currentPageAction';

export default function EnvironmentalAspectsAssessmentAspectCard({ aspectID }) {
  const dispatch = useDispatch();
  const assessment = useSelector((s) => s?.currentPage?.assessment);
  const aspects = useSelector((state) => state.currentPage?.aspects);
  const aspect = useSelector((state) => state.currentPage?.aspects?.[aspectID]);
  const checkedCards = useSelector((state) => state.currentPage?.checkedCards);
  const [showAssistantModal, setShowAssistantModal] = useState(false);
  const renderEvaluationText = () => {
    if (checkedCards?.includes(aspectID)) {
      if (aspect?.evaluation !== null) {
        return aspect?.evaluation >= 1 ? 'Significativo' : 'No significativo';
      }

      return 'No evaluado';
    }

    return 'No aplica';
  };

  const renderEvaluationColor = () => {
    if (checkedCards?.includes(aspectID)) {
      if (aspect?.evaluation !== null) {
        return aspect?.evaluation >= 1 ? 'text-danger' : 'text-warning';
      }

      return 'text-info';
    }

    return 'text-success';
  };

  const handleCalculation = useCallback(
    (calculationID, settings) => {
      switch (calculationID) {
        case 1:
          return Object.entries(settings).reduce((acc, [key, val]) => {
            let selectedVal = val;
            const input = aspect?.inputs?.find((i) => i?.id == key);

            switch (input?.type) {
              case 1:
                selectedVal = input?.settings?.options?.find(
                  (i) => i?.id == val,
                )?.value;
                break;
            }

            return acc + (selectedVal ?? 1);
          }, 0);
        case 2:
        case 3:
        case 4:
          return Object.entries(settings).reduce((acc, [key, val]) => {
            let selectedVal = val;
            const input = aspect?.inputs?.find((i) => i?.id == key);

            switch (input?.type) {
              case 1:
                selectedVal = input?.settings?.options?.find(
                  (i) => i?.id == val,
                )?.value;
                break;
            }

            return acc + (selectedVal ?? 0);
          }, 0);
      }
    },
    [aspect],
  );

  const handleFieldValue = (field, v) => {
    // Copia de aspects
    const updatedAspects = { ...aspects };
    // Copia del aspect
    const prevAspect = updatedAspects[aspectID];
    const updatedAspect = { ...prevAspect };

    // Copia PROFUNDA de settings (al menos 1 nivel)
    const prevSettings =
      updatedAspect.settings && !Array.isArray(updatedAspect.settings)
        ? updatedAspect.settings
        : {};

    updatedAspect.settings = { ...prevSettings };

    // Modificar sobre la copia
    if (v === '' || v === null || Number.isNaN(v)) {
      delete updatedAspect.settings[field];
    } else {
      updatedAspect.settings[field] = Number(v);
    }

    // Recalcular
    if (Object.keys(updatedAspect.settings).length > 0) {
      updatedAspect.evaluationResult = handleCalculation(
        updatedAspect?.calculation?.id,
        updatedAspect.settings,
      );
    } else {
      updatedAspect.evaluationResult = null;
    }

    if (
      Object.keys(updatedAspect.settings).length === Object.keys(aspect.inputs).length
    ) {
      updatedAspect.evaluation =
        updatedAspect.evaluationResult >= (updatedAspect?.calculation?.threshold ?? 2)
          ? 1
          : 0;
    } else {
      updatedAspect.evaluation = null;
      updatedAspect.evaluationResult = null;
    }

    // Guardar el aspect actualizado dentro del array/objeto
    updatedAspects[aspectID] = updatedAspect;

    // ✅ Ahora sí: dispatch con estado nuevo (sin mutar el anterior)
    dispatch(currentPageAction.change('aspects', updatedAspects, true));
  };

  const renderInputs = () =>
    aspect?.inputs?.map((i) => {
      switch (i?.type) {
        case 0:
          return checkedCards?.includes(aspectID) && assessment?.closed === false ? (
            <AppInputNumber
              title={i?.name}
              value={aspect?.settings?.[i?.id] ?? ''}
              onChange={(v) => handleFieldValue(i?.id, v)}
              isClearable
            />
          ) : (
            <AppInput
              title={i?.name}
              value={aspect?.settings?.[i?.id] ?? ''}
              isDisabled
            />
          );
        case 1:
          return checkedCards?.includes(aspectID) && assessment?.closed === false ? (
            <AppSingleSelect
              options={i?.settings?.options || []}
              title={i?.name}
              value={aspect?.settings?.[i?.id] ?? null}
              onChange={(v) => handleFieldValue(i?.id, v)}
              setValueField="id"
              isClearable
            />
          ) : (
            <AppInput
              title={i?.name}
              value={
                i?.settings?.options?.find((o) => o?.id === aspect?.settings?.[i?.id])
                  ?.label ?? ''
              }
              isDisabled
            />
          );
        default:
          return <></>;
      }
    });

  return (
    <Container
      className="mb-2"
      bodyClassName="p-1"
      $apply={checkedCards?.includes(aspectID)}
    >
      <div className="info-block">
        <div className="line mb-2">
          <div>
            Aspecto:
            {aspect?.assistants?.length > 0 && (
              <AppRemixIcon
                title="Asistente"
                className="pointer ml-1"
                icon="information"
                color="#00ff00"
                onClick={() => setShowAssistantModal(true)}
                size={17}
              />
            )}
          </div>
          <div>
            <b>{aspect?.aspect}</b>
          </div>
        </div>
        <div className="line mb-2">
          <div>Descripcion:</div>
          <div>
            <b>{aspect?.description}</b>
          </div>
        </div>
      </div>
      <div className="comment-block">
        <div className="line">Observaciones:</div>
        <div className="line">
          <b>{aspect?.comment || '--'}</b>
        </div>
      </div>
      <div className="input-block">{renderInputs()}</div>
      <div className="score-block">
        <div className="line mb-2">
          <div>Impacto medio:</div>
          <div>
            <b>{aspect?.evaluationResult ?? 'n.a.'}</b>
          </div>
        </div>
        <div className="line mb-2">
          <div>
            <Translator file="module-comsaEnvironmental" name="assessment" />
          </div>
          <div>
            <b className={renderEvaluationColor()}>{renderEvaluationText()}</b>
          </div>
        </div>
      </div>
      {aspect?.assistants?.length > 0 && (
        <AppModal
          title="Asistente"
          show={showAssistantModal}
          onClose={() => setShowAssistantModal(false)}
        >
          <div className="container-fluid">
            <div className="row">
              {aspect?.assistants?.map(
                (a, i) =>
                  a?.description && (
                    <div key={i} className="col-lg-12">
                      <h6>{a?.tipology}</h6>
                      <div className="mb-3">{a.description}</div>
                    </div>
                  ),
              )}
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-12">
              <button
                type="button"
                onClick={() => setShowAssistantModal(false)}
                className="btn btn-sm btn-primary float-right"
              >
                Cerrar
              </button>
            </div>
          </div>
        </AppModal>
      )}
    </Container>
  );
}

const Container = styled(AppCard)`
  background-color: ${(props) => (props.$apply ? '#fff' : colors.grey200)};

  .card-body {
    display: grid;
    grid-template-columns: 1fr 250px 1fr 250px;
  }
  .info-block,
  .input-block,
  .score-block,
  .comment-block {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 0.5rem;
  }

  .info-block,
  .score-block {
    text-align: center;
  }
  .info-block,
  .score-block,
  .comment-block {
    .line {
      color: ${colors.primaryLight};
      span:first-child {
        color: #000;
        width: 180px;
        display: inline-block;
      }
    }
  }
  .info-block {
    @media (min-width: 1300px) {
      flex-direction: row;
    }
    .line {
      flex: 1 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }
  .input-block {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  .score-block {
    flex-direction: column;
    justify-content: center;
    .line {
      min-height: 42px;
      &.no-opacity {
        opacity: 0;
      }
    }
  }
  .comment-block {
  }
  h3 {
    font-weight: normal;
    color: ${colors.primaryLight};
  }
  h3 {
    font-weight: normal;
    color: ${colors.primaryLight};
  }
  & > div {
    border-right: 1px solid ${colors.grey300};
  }
  h3 {
    font-size: 13px;
    color: ${colors.primary};
    margin: 0 0 6px 0;
    font-weight: bold;
  }
  h4 {
    font-size: 10px;
    color: $primary;
    text-transform: uppercase;
  }
`;
